import { useCallback, useEffect, useRef, useState } from 'react';
import { gamesApi, getSocket, onSocketJoin } from '../../services/api/gamesApi';
import { playDiceWinning, playTokenDie } from './ludoSounds.js';

function tokenBoardFingerprint(tokens) {
  if (!tokens || typeof tokens !== 'object') return '';
  return Object.keys(tokens)
    .sort()
    .map((color) =>
      (tokens[color] || [])
        .map((t) => `${t.step}${t.finished ? 'f' : 'b'}`)
        .join('.'),
    )
    .join('/');
}

function boardEventSoundKey(event, tokens) {
  if (!event?.type) return '';
  if (event.at != null) return `${event.type}:${event.at}`;
  return `${event.type}:${tokenBoardFingerprint(tokens)}`;
}

export function useLudoPvP(roomId, userId) {
  const [room, setRoom] = useState(null);
  const [isRolling, setIsRolling] = useState(false);
  const [log, setLog] = useState([]);
  const [ended, setEnded] = useState(null);
  const [lastRolledValue, setLastRolledValue] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [rollSecondsLeft, setRollSecondsLeft] = useState(null);
  const [opponentRollSecondsLeft, setOpponentRollSecondsLeft] = useState(null);
  const [rollGraceSecondsLeft, setRollGraceSecondsLeft] = useState(null);
  const [opponentRollGraceSecondsLeft, setOpponentRollGraceSecondsLeft] = useState(null);
  const [isBotRolling, setIsBotRolling] = useState(false);
  const syncSentRef = useRef(false);
  const rollSyncSentRef = useRef(false);
  const playedSoundKeysRef = useRef(new Set());
  const soundsPrimedRef = useRef(false);
  const localRollingRef = useRef(false);
  const boardInitializedRef = useRef(false);
  const prevDiceRef = useRef(undefined);
  const [opponentRolling, setOpponentRolling] = useState(false);

  const pushLog = useCallback((msg) => {
    setLog((prev) => [msg, ...prev].slice(0, 50));
  }, []);

  const applyRoom = useCallback((nextRoom) => {
    setRoom(nextRoom);
    const event = nextRoom?.board?.lastEvent;
    if (event?.type === 'capture') pushLog('Token captured!');
    if (event?.type === 'safe_landing') pushLog('Token reached a safe square.');
    if (event?.type === 'finish') pushLog('Token reached home!');
    if (event?.type === 'break_stack') pushLog('Block broken — remaining token sent to base.');
    if (event?.type === 'auto_move') pushLog('Only one move — token moved automatically.');
    if (event?.type === 'timeout_auto_move') pushLog('Time up — token moved automatically.');
    if (event?.type === 'timeout_forfeit') pushLog('Too many timeouts — game forfeited.');
    if (event?.type === 'roll_timeout_forfeit') pushLog('Dice roll timer expired — game forfeited.');
  }, [pushLog]);

  useEffect(() => {
    if (!roomId) return undefined;
    setEnded(null);
    boardInitializedRef.current = false;
    prevDiceRef.current = undefined;
    playedSoundKeysRef.current = new Set();
    soundsPrimedRef.current = false;
    gamesApi.ludoGetRoom(roomId).then((data) => applyRoom(data.room)).catch(() => {});
    const cleanup = onSocketJoin((socket) => {
      socket.emit('join:ludo', { roomId });
    });
    const socket = getSocket();
    const onState = (payload) => {
      if (payload?.id === roomId) {
        syncSentRef.current = false;
        applyRoom(payload);
      }
    };
    const onEnded = (payload) => {
      if (payload?.roomId && payload.roomId !== roomId) return;
      setEnded(payload);
    };
    socket.on('ludo:state', onState);
    socket.on('ludo:ended', onEnded);
    return () => {
      cleanup();
      socket.emit('leave:ludo', { roomId });
      socket.off('ludo:state', onState);
      socket.off('ludo:ended', onEnded);
    };
  }, [roomId, applyRoom]);

  const board = room?.board;
  const myColor = room?.myColor;
  const tokens = board?.tokens ?? {};
  const movableTokenIds = (room?.legalMoves ?? []).map((idx) => `${myColor}-${idx}`);
  const currentColor = board?.currentColor;
  const boardDice = board?.dice ?? null;
  const dice = lastRolledValue ?? boardDice;
  const mustMove = boardDice != null && movableTokenIds.length > 0 && board?.phase === 'MOVE';
  const isMyTurn = Boolean(room?.isMyTurn);
  const diceDisabled = isRolling || mustMove || !isMyTurn || board?.phase !== 'ROLL';
  const moveDeadlineAt = room?.moveDeadlineAt ?? board?.moveDeadlineAt ?? null;
  const rollCountdownStartsAt = room?.rollCountdownStartsAt ?? board?.rollCountdownStartsAt ?? null;
  const rollDeadlineAt = room?.rollDeadlineAt ?? board?.rollDeadlineAt ?? null;
  const phase = board?.phase ?? 'ROLL';
  const myTimeoutCount = room?.myTimeoutCount ?? 0;
  const opponentTimeoutCount = room?.opponentTimeoutCount ?? 0;
  const maxMoveTimeouts = room?.maxMoveTimeouts ?? 3;

  useEffect(() => {
    if (board?.dice != null) {
      setLastRolledValue(board.dice);
    } else if (board?.phase === 'ROLL') {
      setLastRolledValue(null);
    }
  }, [board?.dice, board?.phase]);

  useEffect(() => {
    if (!board) return;

    if (!boardInitializedRef.current) {
      boardInitializedRef.current = true;
      prevDiceRef.current = board.dice;
      return;
    }

    const prevDice = prevDiceRef.current;
    prevDiceRef.current = board.dice;

    if (board.dice == null || board.dice === prevDice) return;
    if (localRollingRef.current || isRolling) return;
    if (isMyTurn || room?.isBotGame) return;

    setOpponentRolling(true);
    const timer = window.setTimeout(() => setOpponentRolling(false), 560);
    return () => window.clearTimeout(timer);
  }, [board, board?.dice, isMyTurn, isRolling, room?.isBotGame]);

  useEffect(() => {
    if (!board) return;

    const event = board.lastEvent;
    const soundKey = boardEventSoundKey(event, board.tokens);

    if (!soundsPrimedRef.current) {
      soundsPrimedRef.current = true;
      if (soundKey) playedSoundKeysRef.current.add(soundKey);
      return;
    }

    if (!event || !soundKey) return;
    if (playedSoundKeysRef.current.has(soundKey)) return;
    playedSoundKeysRef.current.add(soundKey);

    if (event.type === 'capture') playTokenDie();
    if (event.type === 'finish') playDiceWinning();
  }, [board, board?.lastEvent, board?.tokens]);

  useEffect(() => {
    rollSyncSentRef.current = false;
    if (phase !== 'ROLL' || !rollDeadlineAt) {
      setRollSecondsLeft(null);
      setOpponentRollSecondsLeft(null);
      setRollGraceSecondsLeft(null);
      setOpponentRollGraceSecondsLeft(null);
      return undefined;
    }

    const tick = () => {
      const now = Date.now();
      const inGrace = rollCountdownStartsAt != null && now < rollCountdownStartsAt;
      const graceLeft = inGrace
        ? Math.max(0, Math.ceil((rollCountdownStartsAt - now) / 1000))
        : null;
      const left = Math.max(0, Math.ceil((rollDeadlineAt - now) / 1000));

      if (isMyTurn) {
        setOpponentRollSecondsLeft(null);
        setOpponentRollGraceSecondsLeft(null);
        if (inGrace) {
          setRollGraceSecondsLeft(graceLeft);
          setRollSecondsLeft(null);
        } else {
          setRollGraceSecondsLeft(null);
          setRollSecondsLeft(left);
        }
      } else {
        setRollSecondsLeft(null);
        setRollGraceSecondsLeft(null);
        if (inGrace) {
          setOpponentRollGraceSecondsLeft(graceLeft);
          setOpponentRollSecondsLeft(null);
        } else {
          setOpponentRollGraceSecondsLeft(null);
          setOpponentRollSecondsLeft(left);
        }
      }

      if (left === 0 && !rollSyncSentRef.current) {
        rollSyncSentRef.current = true;
        gamesApi.ludoSync(roomId).then((data) => applyRoom(data.room)).catch(() => {
          rollSyncSentRef.current = false;
        });
      }
    };

    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [rollDeadlineAt, rollCountdownStartsAt, phase, isMyTurn, roomId, applyRoom]);

  useEffect(() => {
    syncSentRef.current = false;
    if (!moveDeadlineAt || phase !== 'MOVE') {
      setSecondsLeft(null);
      return undefined;
    }

    const tick = () => {
      const left = Math.max(0, Math.ceil((moveDeadlineAt - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left === 0 && !syncSentRef.current) {
        syncSentRef.current = true;
        gamesApi.ludoSync(roomId).then((data) => applyRoom(data.room)).catch(() => {
          syncSentRef.current = false;
        });
      }
    };

    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [moveDeadlineAt, phase, roomId, applyRoom]);

  const rollDice = async () => {
    if (diceDisabled || !roomId) return;
    localRollingRef.current = true;
    setIsRolling(true);
    const started = Date.now();
    try {
      const result = await gamesApi.ludoRoll(roomId);
      const elapsed = Date.now() - started;
      const minRollMs = 560;
      if (elapsed < minRollMs) {
        await new Promise((resolve) => window.setTimeout(resolve, minRollMs - elapsed));
      }
      setLastRolledValue(result.dice);
      applyRoom(result.room);
      pushLog(`Rolled ${result.dice}`);
    } catch (err) {
      pushLog(err.message || 'Could not roll');
    } finally {
      setIsRolling(false);
      window.setTimeout(() => {
        localRollingRef.current = false;
      }, 200);
    }
  };

  const moveToken = async (tokenId) => {
    if (!movableTokenIds.includes(tokenId) || !roomId) return;
    const tokenIndex = Number(tokenId.split('-').pop());
    try {
      const result = await gamesApi.ludoMove(roomId, tokenIndex);
      applyRoom(result.room);
    } catch (err) {
      pushLog(err.message || 'Could not move');
    }
  };

  const leaveGame = async () => {
    if (!roomId) return null;
    return gamesApi.ludoLeave(roomId);
  };

  const winners = board?.winner ? [board.winner] : [];

  const opponentColor =
    room?.hostColor && room?.guestColor
      ? myColor === room.hostColor
        ? room.guestColor
        : room.hostColor
      : null;

  const isBotGame = Boolean(room?.isBotGame);

  useEffect(() => {
    if (!isBotGame || isMyTurn || !opponentColor) {
      setIsBotRolling(false);
      return;
    }
    if (board?.currentColor === opponentColor && board?.phase === 'ROLL') {
      setIsBotRolling(true);
    } else {
      setIsBotRolling(false);
    }
  }, [isBotGame, isMyTurn, opponentColor, board?.currentColor, board?.phase]);

  return {
    room,
    tokens,
    currentColor,
    myColor,
    dice,
    isRolling: isRolling || isBotRolling || opponentRolling,
    movableTokenIds,
    winners,
    log,
    lastEvent: board?.lastEvent ?? null,
    isMyTurn,
    mustMove,
    diceDisabled,
    secondsLeft,
    rollSecondsLeft,
    opponentRollSecondsLeft,
    rollGraceSecondsLeft,
    opponentRollGraceSecondsLeft,
    phase,
    myTimeoutCount,
    opponentTimeoutCount,
    maxMoveTimeouts,
    ended,
    rollDice,
    moveToken,
    leaveGame,
    stake: room?.stake ?? 0,
    opponent: room?.host?.id === userId ? room?.guest : room?.host,
    opponentColor,
    isBotGame,
    platformCommissionPercent: room?.platformCommissionPercent ?? 7,
    winnerPayout: room?.winnerPayout ?? 0,
  };
}
